// Rotating welcome messages shown on the profile screen — admin-editable, like
// career paths and policies. A deterministic hash of the user's name picks
// one (stable per person) from whichever list is currently active.

import { getAdminToken, getSetting, getPublic, putSetting } from "../lib/api";

export interface WelcomeMessage {
  ar: string;
  en: string;
}

export const DEFAULT_WELCOME_MESSAGES: WelcomeMessage[] = [
  {
    ar: "نشعر بسعادة بالغة لأنك قررت الانضمام إلى شركتنا، مرحباً بك بيننا وتمنياتنا لك بالتوفيق في مهمتك الجديدة.",
    en: "We are delighted that you have decided to join our company. Welcome aboard, and best wishes for success in your new role.",
  },
  {
    ar: "أهلاً بك في كادن للاستثمار، نأمل أن تشعر بالراحة بيننا، ونثق أننا سنعمل معاً بشكل جيد طوال فترة عملك معنا.",
    en: "Welcome to Kaden Investment. We hope you feel comfortable here, and we trust we will work together well throughout your journey with us.",
  },
  {
    ar: "مبارك لك عملك الجديد، ونرحب بك للعمل معنا. إذا احتجت إلى أي مساعدة أو لديك أي سؤال أو اقتراح فلا تتردد، فنحن هنا جميعاً فريقاً واحداً.",
    en: "Congratulations on your new role. If you need any help, have a question, or wish to share an idea, do not hesitate; we are all one team here.",
  },
  {
    ar: "نود أن نرحب بك في بيتك الثاني، ونعرب لك عن سعادتنا البالغة بوجودك في كادن، ونأمل رؤية إسهاماتك حتى نحقق أهدافنا معاً.",
    en: "We welcome you to your second home, and we are very glad to have you with us at Kaden. We look forward to your contributions as we achieve our goals together.",
  },
  {
    ar: "مرحباً بك، نشعر بالفخر لأنك أصبحت جزءاً من فريقنا، ونرى أنك ستكون إضافة رائعة لنا، مع أطيب أمنياتنا بالنجاح والتوفيق.",
    en: "Welcome, we are proud to have you on the team and see you as a wonderful addition. Our warmest wishes for your success.",
  },
  {
    ar: "نشكرك على قبولك عرض عملنا، ونثق بشدة أن مساهماتك معنا ستكون سبباً في نجاح شركتنا. إذا احتجت إلى المساعدة فتواصل معنا على الفور.",
    en: "Thank you for accepting our offer. We are confident your contributions will be key to our success. Reach out to us anytime you need help.",
  },
  {
    ar: "أهلاً وسهلاً بك في كادن، نؤمن أنك ستكون إضافة مثالية لفريقنا بفضل موهبتك وخبراتك الرائعة. في انتظار إنجازاتك معنا.",
    en: "Welcome to Kaden. We believe you will be a perfect addition to our team thanks to your talent and experience. We look forward to your achievements.",
  },
  {
    ar: "مرحباً بك، نأمل أن يكون عملك معنا تجربة رائعة تحقق منها أقصى استفادة، ونأمل أن تستفيد الشركة من مواهبك النادرة.",
    en: "Welcome. We hope your time with us will be an excellent experience from which you gain the most, and that the company will benefit from your unique talents.",
  },
  {
    ar: "أرحب بك نيابة عن فريقنا في الشركة، وأنقل لك تهاني زملائك في الفريق بعملك الجديد، وأمنياتهم لك بالتوفيق والسداد.",
    en: "On behalf of our team, I welcome you and pass along the congratulations of your new colleagues, with their wishes for success and the right path forward.",
  },
  {
    ar: "أهلاً بك بيننا، نحن متحمسون للغاية للعمل معاً، وكلنا ثقة بأنك ستساهم في وصول الشركة إلى أهدافها المنشودة.",
    en: "Welcome among us. We are thrilled to work together, and we are confident you will help the company reach its goals.",
  },
  {
    ar: "نحن نؤمن بأنك ستكون إضافة قيمة لفريقنا، ومهاراتك وخبراتك ستساهم في تعزيز أداء الشركة وتحقيق أهدافنا. نحن هنا لدعمك في كل خطوة على طريق النجاح.",
    en: "We believe you will be a valuable addition to our team, and your skills will help strengthen the company's performance. We are here to support you every step of the way.",
  },
];

/** Deterministic pick based on a string hash (stable per name). */
export function pickWelcomeMessage(name: string): WelcomeMessage {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  const list = getWelcomeMessages();
  if (list.length === 0) return DEFAULT_WELCOME_MESSAGES[0];
  return list[Math.abs(hash) % list.length];
}

// --- Runtime store (admin can override via the "welcome_messages" setting) ---
const MESSAGES_CHANGED = "kd-welcome-messages-changed";
let current: WelcomeMessage[] = [...DEFAULT_WELCOME_MESSAGES];

export function getWelcomeMessages(): WelcomeMessage[] {
  return current;
}

export function subscribeWelcomeMessages(cb: () => void): () => void {
  window.addEventListener(MESSAGES_CHANGED, cb);
  return () => window.removeEventListener(MESSAGES_CHANGED, cb);
}

function emit() {
  try {
    window.dispatchEvent(new Event(MESSAGES_CHANGED));
  } catch {
    /* ignore */
  }
}

/** Load any admin-configured message overrides (public read, or admin read when signed in). */
export async function loadWelcomeMessages(): Promise<void> {
  const res = getAdminToken()
    ? await getSetting<WelcomeMessage[]>("welcome_messages")
    : await getPublic<{ value?: WelcomeMessage[] } | WelcomeMessage[]>("welcome_messages");
  const value = Array.isArray((res.data as any)?.value) ? (res.data as any).value : res.data;
  if (Array.isArray(value) && value.length > 0) current = value as WelcomeMessage[];
  emit();
}

// --- Admin editing ---
export async function saveWelcomeMessages(messages: WelcomeMessage[]): Promise<{ ok: boolean; error?: string }> {
  current = messages;
  emit();
  const res = await putSetting("welcome_messages", messages);
  return res.ok ? { ok: true } : { ok: false, error: res.error };
}
export function resetWelcomeMessages(): Promise<{ ok: boolean; error?: string }> {
  return saveWelcomeMessages([...DEFAULT_WELCOME_MESSAGES]);
}
